import moment from "moment";
import companyService from "@/services/company.js";
import restaurantService from "@/services/restaurant.js";
import reportService from "@/services/report.js";

const DEFAULT_FILTERS = {
  dates: {
    from: moment().format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  },
  topupSource: ["company", "employee"],
  paymentMethod: ["automatic", "card", "cash", "bank_transfer"],
  search: "",
  selectedShifts: [1, 2, 3],
  warehouse: null,
  showType: "daily",
  category: null,
  inStock: null,
  showCompanyNames: true,
  employeeStatus: 0, // status porudžbe u izveštaju Radnici (firma "više restorana")
};

const state = () => ({
  reportFood: null,
  items: [],
  foodCompanies: [],
  mealCompanies: [],
  restaurantMultiCompanies: [],
  allCompanies: [], // Cache for all companies
  filters: { ...DEFAULT_FILTERS },
  selectedCompany: null,
  selectedMealCompany: null,
  selectedMealCompanies: [],
  selectedEmployee: null, // radnik otvoren u mobilnom mesečnom pregledu
  selectedFoodCompanies: [],
  selectedRestaurants: [], // For multi-restaurant report
  selectedBranches: [], // Poslovnice firme "više restorana" (prazno = sve)
  selectedRestaurant: null, // For single restaurant filter (employee meal summary)
  loading: {
    fetchMealCompanies: false,
    fetchFoodCompanies: false,
    fetchRestaurantMultiCompanies: false,
  },
  reload: false,
  error: null,
  lastFetchParams: null, // Store last fetch parameters to avoid duplicate fetches
});

const mutations = {
  SET_RELOAD(state) {
    state.reload = !state.reload;
  },
  SET_SELECTED_FOOD_COMPANIES(state, companies) {
    state.selectedFoodCompanies = Array.isArray(companies) ? companies : [];
  },
  SET_SELECTED_RESTAURANTS(state, restaurants) {
    state.selectedRestaurants = Array.isArray(restaurants) ? restaurants : [];
  },
  SET_SELECTED_BRANCHES(state, branches) {
    state.selectedBranches = Array.isArray(branches) ? branches : [];
  },
  SET_SELECTED_RESTAURANT(state, restaurant) {
    state.selectedRestaurant = restaurant;
  },
  SET_SELECTED_EMPLOYEE(state, employee) {
    state.selectedEmployee = employee || null;
  },
  SET_SELECTED_MEAL_COMPANIES(state, companies) {
    state.selectedMealCompanies = Array.isArray(companies) ? companies : [];
  },
  SET_SELECTED_MEAL_COMPANY(state, company) {
    state.selectedMealCompany = company;
  },
  SET_MEAL_COMPANIES(state, companies) {
    state.mealCompanies = Array.isArray(companies) ? companies : [];
  },
  SET_RESTAURANT_MULTI_COMPANIES(state, companies) {
    state.restaurantMultiCompanies = Array.isArray(companies) ? companies : [];
  },
  SET_FOOD_COMPANIES(state, companies) {
    state.foodCompanies = Array.isArray(companies) ? companies : [];
  },
  SET_ALL_COMPANIES(state, companies) {
    state.allCompanies = Array.isArray(companies) ? companies : [];
  },
  SET_SELECTED_COMPANY(state, company) {
    state.selectedCompany = company;
  },
  SET_REPORT_FOOD(state, data) {
    state.reportFood = data;
  },
  SET_ITEMS(state, items) {
    state.items = Array.isArray(items) ? items : [];
  },
  SET_DATE_RANGE(state, { from, to }) {
    if (moment(from).isValid() && moment(to).isValid()) {
      state.filters.dates.from = from;
      state.filters.dates.to = to;
    }
  },
  SET_FILTER(state, { key, value }) {
    if (key in state.filters) {
      state.filters[key] = value;
    }
  },
  RESET_FILTERS(state) {
    state.filters = { ...DEFAULT_FILTERS };
  },
  SET_LOADING(state, { operation, loading }) {
    if (operation in state.loading) {
      state.loading[operation] = loading;
    }
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
};

const actions = {
  async fetchMealCompanies({ commit, state }, payload) {
    commit("SET_LOADING", { operation: "fetchMealCompanies", loading: true });
    try {
      // Get companies from state cache or fetch if not available
      let companiesResponse;
      if (state.allCompanies.length > 0) {
        // Use cached companies
        companiesResponse = { data: state.allCompanies };
      } else {
        // Fetch and cache companies
        companiesResponse = await companyService.getCompanies();
      }

      // Flatten companies including children. Poslovnica koja je i sama vezana za restoran stiže i kao samostalna firma
      // i kao dijete matične, pa se isti id ne sme ponoviti (dupli redovi u izboru firmi); zapisi se spajaju.
      const flattenCompanies = (companies) => {
        const byId = new Map();
        const add = (entry) => {
          const known = byId.get(entry.id);
          if (!known) {
            byId.set(entry.id, entry);
            return;
          }
          Object.keys(entry).forEach((key) => {
            if (known[key] === undefined) {
              known[key] = entry[key];
            }
          });
        };
        companies.forEach((company) => {
          // Add parent company
          add({
            id: company.id,
            name: company.name,
            contract: company.contract
          });
          // Add children if they exist
          if (company.children && Array.isArray(company.children)) {
            company.children.forEach((child) => {
              add({
                id: child.id,
                name: child.name,
                parentId: company.id,
              });
            });
          }
        });
        return Array.from(byId.values());
      };

      const mealCompanies = flattenCompanies(companiesResponse.data);
      commit("SET_MEAL_COMPANIES", mealCompanies);
    } catch (error) {
      commit("SET_ERROR", error.response?.data?.message || error.message);
    } finally {
      commit("SET_LOADING", {
        operation: "fetchMealCompanies",
        loading: false,
      });
    }
  },

  async fetchRestaurantMultiCompanies({ commit }) {
    commit("SET_LOADING", { operation: "fetchRestaurantMultiCompanies", loading: true });
    try {
      const { data } = await companyService.getCompanies();
      const filtered = (Array.isArray(data) ? data : []).filter((c) => c.contract == 4);
      commit("SET_RESTAURANT_MULTI_COMPANIES", filtered);
    } catch (error) {
      commit("SET_ERROR", error.response?.data?.message || error.message);
    } finally {
      commit("SET_LOADING", { operation: "fetchRestaurantMultiCompanies", loading: false });
    }
  },

  async fetchFoodCompanies({ commit, state }, payload) {
    if (!payload?.restaurant_id) return;

    // Check if we already have data for these params
    const currentParams = JSON.stringify({
      restaurant_id: payload.restaurant_id,
      date_from: payload.date_from,
      date_to: payload.date_to,
    });

    if (state.lastFetchParams === currentParams && state.foodCompanies.length > 0) {
      // Data already fetched for these params, skip
      return;
    }

    commit("SET_LOADING", { operation: "fetchFoodCompanies", loading: true });
    try {
      const params = { date: payload.date_from, date2: payload.date_to };
      const { data } = await restaurantService.getGroupType(payload.restaurant_id, params);

      commit("SET_REPORT_FOOD", data);
      commit(
        "SET_FOOD_COMPANIES",
        data || []
      );

      // Store params to prevent duplicate fetches
      state.lastFetchParams = currentParams;
    } catch (error) {
      commit("SET_ERROR", error.response?.data?.message || error.message);
    } finally {
      commit("SET_LOADING", {
        operation: "fetchFoodCompanies",
        loading: false,
      });
    }
  },

  setFilter({ commit }, { key, value }) {
    commit("SET_FILTER", { key, value });
  },

  setDateRange({ commit }, { from, to }) {
    commit("SET_DATE_RANGE", { from, to });
  },

  resetFilters({ commit }) {
    commit("RESET_FILTERS");
  },

};

const getters = {
  filteredItems: (state) => {
    let items = state.items || [];

    if (state.filters.search) {
      const searchTerm = state.filters.search.toLowerCase();
      items = items.filter((item) =>
        item?.name?.toLowerCase().includes(searchTerm)
      );
    }

    if (state.filters.category) {
      items = items.filter((item) => item.category === state.filters.category);
    }

    if (state.filters.inStock !== null) {
      items = items.filter((item) => item.inStock === state.filters.inStock);
    }

    return items;
  },
  // Getter za firme sa ugovorom sa više restorana (contract == 4)
  multiRestaurantMealCompanies: (state) => {
    return (state.mealCompanies || []).filter((company) => company.contract == 4);
  },
  restaurantMultiCompanies: (state) => state.restaurantMultiCompanies,
  filters: (state) => state.filters,
  loading: (state) => state.loading,
  isLoading: (state) => (operation) => state.loading[operation] || false,
  error: (state) => state.error,
  hasData: (state) => Boolean(state.reportFood || state.items?.length),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
